import { Controller } from '@hotwired/stimulus';
import {
  updateDocumentUserState,
  extractHiddenAnswerCommentHTML,
  hasAnswerCommentAvailable,
  restoreAnswerCommentHTML,
  noteSaveEpochsSettled,
  removeSpeechToTextToolbarFromContainer,
  shouldPreserveAnswerCommentInDom,
  findSubmitButtonContainerForElement,
  syncSubmitButtonInactiveState,
} from '../utils/case_experience_in_rails';

export default class extends Controller {
  static targets = ['textarea', 'charCount', 'container', 'disclaimer'];

  static values = {
    documentId: Number,
    userId: Number,
    nodeUid: String,
    boxLines: { type: Number, default: 5 },
    characterLimit: { type: Number, default: 1000 },
    charCount: Number,
    isSummaryStatement: Boolean,
    graded: Boolean,
    isAiSummaryFeedbackEnabled: Boolean,
  };

  connect() {
    this.fromFeedbackModal = this.element.hasAttribute('data-feedback-modal');
    this._noteEditEpoch = 0;
    this._noteSaveInFlightCount = 0;
    this.saveTimeout = null;
    this._charCountAnnounceTimeout = null;

    this.setupTextareaPersistUi();
    this.bindSubmitButtonClick();
    this.setupInitialState();
  }

  setupTextareaPersistUi() {
    if (!this.hasTextareaTarget) {
      return;
    }

    if (!this._noteInputHandler) {
      this._noteInputHandler = () => {
        const button = this.resolveSubmitButton();
        if (button) {
          if (this.fromFeedbackModal) {
            if (this._feedbackModalSubmitDisabledBeforeInput === undefined) {
              this._feedbackModalSubmitDisabledBeforeInput = button.disabled;
            }
          }
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
        }
      };
      this.textareaTarget.addEventListener('input', this._noteInputHandler);
    }

    if (!this._noteSavedHandler) {
      this._noteSavedHandler = (ev) => {
        const button = this.resolveSubmitButton();
        if (this.fromFeedbackModal) {
          if (button) {
            button.disabled =
              this._feedbackModalSubmitDisabledBeforeInput ?? button.disabled;
            this._feedbackModalSubmitDisabledBeforeInput = undefined;
            button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
          }
          return;
        }
        if (
          button &&
          !this.gradedValue &&
          this.textareaTarget.value?.trim() &&
          noteSaveEpochsSettled(ev.detail)
        ) {
          button.disabled = false;
          button.setAttribute('aria-disabled', 'false');
        }
      };
      this.textareaTarget.addEventListener('note:saved', this._noteSavedHandler);
    }

    this.textareaTarget.setAttribute('maxlength', this.characterLimitValue);
    this.updateCharCount();
    setTimeout(() => this.updateSubmitButtonState(), 0);
  }

  findSubmitButtonContainer() {
    return findSubmitButtonContainerForElement(this.element);
  }

  resolveSubmitButton() {
    if (this._submitButton?.isConnected) {
      return this._submitButton;
    }

    const container = this.findSubmitButtonContainer();
    this._submitButtonContainer = container;
    this._submitButton = container?.querySelector('button') ?? null;
    return this._submitButton;
  }

  bindSubmitButtonClick() {
    this._handleSubmitClickBound = this.handleSubmitClick.bind(this);
    const button = this.resolveSubmitButton();
    if (button) {
      button.addEventListener('click', this._handleSubmitClickBound);
    }
  }

  setupInitialState() {
    if (
      !this.gradedValue &&
      !this.isAiSummaryFeedbackEnabledValue &&
      !shouldPreserveAnswerCommentInDom()
    ) {
      this.storeAnswerCommentHTML();
    }
    this.hideOrShowGradedFeedback();
    if (this.hasTextareaTarget && this.gradedValue && !this.fromFeedbackModal) {
      this.textareaTarget.disabled = true;
    }
  }

  hasVisibleAnswerCommentSection() {
    let sibling = this.element.parentElement?.firstElementChild;
    while (sibling) {
      if (
        sibling !== this.element &&
        sibling.classList &&
        sibling.classList.contains('doc-section') &&
        sibling.classList.contains('answer_comment') &&
        sibling.offsetParent !== null
      ) {
        return true;
      }
      sibling = sibling.nextElementSibling;
    }
    return false;
  }

  isStudentDisplayMode() {
    const docBody = this.element.closest('.doc-body');
    return !!docBody?.classList.contains('student-display-mode');
  }

  storeAnswerCommentHTML() {
    const result = extractHiddenAnswerCommentHTML(this.element);
    if (result) {
      this._answerCommentSibling = result.sibling;
      this.answerCommentHtml = result.html;
    } else {
      this._answerCommentSibling = null;
      this.answerCommentHtml = null;
    }
  }

  hideOrShowGradedFeedback() {
    const gradedFeedback = this.element.querySelector('.graded-feedback');
    const hasAnswerComment = hasAnswerCommentAvailable(this.element, this.answerCommentHtml);
    if (!gradedFeedback || !this.gradedValue) {
      return;
    }
    if (hasAnswerComment) {
      gradedFeedback.innerHTML = this.getGradedFeedbackMessage();
      gradedFeedback.style.display = '';
    } else {
      gradedFeedback.innerHTML = '';
      gradedFeedback.style.display = 'none';
    }
  }

  getGradedFeedbackMessage() {
    if (this.isSummaryStatementValue) {
      return 'Your response will be recorded in your Student Report and may also be viewed by faculty.';
    } else {
      return 'The suggested answer is shown below.';
    }
  }

  updateCharCount() {
    if (!this.hasCharCountTarget || !this.hasTextareaTarget) return;
    const limit = this.characterLimitValue || this.textareaTarget.maxLength || 0;
    if (this.textareaTarget.value.length > limit) {
      this.textareaTarget.value = this.textareaTarget.value.slice(0, limit);
    }
    const count = this.textareaTarget.value.length;
    this.charCountValue = count;
    this.charCountTarget.innerHTML = `${count} / ${limit}`;

    const liveRegion = this.element.querySelector(`#char-count-live-region-${this.nodeUidValue}`);
    if (!liveRegion) return;
    if (document.activeElement !== this.textareaTarget) return;
    if (this._charCountAnnounceTimeout) clearTimeout(this._charCountAnnounceTimeout);
    this._charCountAnnounceTimeout = setTimeout(() => {
      if (!this.hasTextareaTarget || document.activeElement !== this.textareaTarget) return;
      const srMessage = `You have entered ${count} out of ${limit} characters allowed.`;
      liveRegion.setAttribute('aria-live', 'assertive');
      liveRegion.setAttribute('role', 'alert');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.textContent = '';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          liveRegion.textContent = srMessage;
        });
      });
      this._charCountAnnounceTimeout = null;
    }, 900);
  }

  disconnect() {
    if (this.hasTextareaTarget) {
      if (this._noteInputHandler) {
        this.textareaTarget.removeEventListener('input', this._noteInputHandler);
        this._noteInputHandler = null;
      }
      if (this._noteSavedHandler) {
        this.textareaTarget.removeEventListener('note:saved', this._noteSavedHandler);
        this._noteSavedHandler = null;
      }
    }
    if (this._submitButton && this._handleSubmitClickBound) {
      this._submitButton.removeEventListener('click', this._handleSubmitClickBound);
    }
    this._submitButton = null;
    this._submitButtonContainer = null;
    this._handleSubmitClickBound = null;

    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
    if (this._charCountAnnounceTimeout) {
      clearTimeout(this._charCountAnnounceTimeout);
      this._charCountAnnounceTimeout = null;
    }
    if (this.hasTextareaTarget) {
      this.performSave();
    }
  }

  debouncedSave() {
    this._noteEditEpoch += 1;
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }

    this.saveTimeout = setTimeout(() => {
      this.saveTimeout = null;
      this.performSave();
    }, 1000);
  }

  isPersistIdle() {
    return !this.saveTimeout && this._noteSaveInFlightCount === 0;
  }

  reconcileVisibleSubmitButton() {
    this.updateSubmitButtonState(this.isPersistIdle());
  }

  performSave() {
    const saveStartEpoch = this._noteEditEpoch;
    const noteText = this.textareaTarget.value;
    this._noteSaveInFlightCount += 1;

    const body = {
      document_user_state: {
        state: {
          [this.nodeUidValue]: {
            value: { value: noteText, updatedAt: Date.now() },
          },
        },
      },
    };

    updateDocumentUserState({
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      body: body,
    })
      .then(() => {
        this.showSaveIndicator();
        const saveEndEpoch = this._noteEditEpoch;
        const saveSettled = noteSaveEpochsSettled({ saveStartEpoch, saveEndEpoch });
        this.updateSubmitButtonState(saveSettled);
        if (this.hasTextareaTarget) {
          this.textareaTarget.dispatchEvent(
            new CustomEvent('note:saved', {
              bubbles: true,
              detail: { saveStartEpoch, saveEndEpoch },
            }),
          );
        }
      })
      .catch((error) => {
        console.error('Error saving note:', error);
      })
      .finally(() => {
        this._noteSaveInFlightCount = Math.max(0, this._noteSaveInFlightCount - 1);
      });
  }

  updateSubmitButtonState(saveSettled = true) {
    if (this.fromFeedbackModal) {
      return;
    }
    const count = this.textareaTarget?.value?.length || 0;
    const button = this.resolveSubmitButton();
    const submitSlice = this._submitButtonContainer;
    if (!button) return;

    const answerCommentBlocksSubmit =
      this.hasVisibleAnswerCommentSection() && this.isStudentDisplayMode();

    setTimeout(() => {
      if (this.gradedValue) {
        syncSubmitButtonInactiveState(this.application, submitSlice, 2);
        return;
      }
      const inactive =
        count === 0 || answerCommentBlocksSubmit || !saveSettled ? 2 : 1;
      syncSubmitButtonInactiveState(this.application, submitSlice, inactive);
    }, 0);
  }

  handleSubmitClick() {
    const gradedFeedback = this.element.querySelector('.graded-feedback');
    const hasAnswerComment = hasAnswerCommentAvailable(this.element, this.answerCommentHtml);
    if (gradedFeedback) {
      if (hasAnswerComment) {
        gradedFeedback.innerHTML = this.getGradedFeedbackMessage();
        gradedFeedback.style.display = '';
      } else {
        gradedFeedback.innerHTML = '';
        gradedFeedback.style.display = 'none';
      }
    }

    if (this.hasDisclaimerTarget) {
      this.disclaimerTarget.style.display = 'none';
    }

    if (this.hasTextareaTarget) {
      this.textareaTarget.disabled = true;
    }

    if (!this.isAiSummaryFeedbackEnabledValue) {
      this.restoreAnswerCommentHTMLFromVariable();
    }

    removeSpeechToTextToolbarFromContainer(this.element);
  }

  restoreAnswerCommentHTMLFromVariable() {
    restoreAnswerCommentHTML(this.element, this.answerCommentHtml);
  }

  showSaveIndicator() {
    const indicator = document.createElement('span');
    indicator.className = 'save-indicator';
    indicator.textContent = 'Saved';

    const container = this.element;
    const existingIndicator = container.querySelector('.save-indicator');
    if (existingIndicator) {
      existingIndicator.remove();
    }

    container.appendChild(indicator);

    setTimeout(() => {
      indicator.classList.add('save-indicator--visible');
    }, 10);

    setTimeout(() => {
      indicator.classList.remove('save-indicator--visible');
      setTimeout(() => indicator.remove(), 300);
    }, 2000);
  }
}
